import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { useYupValidationResolver } from "@ryde/redux";
import {
  IApiError,
  IDropdownItem,
  IDropdownItemStations,
  isTranslatableItem,
  Log,
  normalizeErrors,
} from "@ryde/shared";
import {
  apiDataFetchingLandingAtom,
  ClalChooseStationStepFormFieldsEnum,
  createMessage,
  IClalChooseStationLandingModel,
  LANDING_DESTINATION_STATIONS_IDS,
  LANDING_WORKERS_TYPE_IDS,
  LandingCompanyNameEnum,
  landingDropdownStationListAtom,
  landingMessagesAtom,
  LandingStepEnum,
  useLandingActions,
  useLandingApiStations,
  useLandingApiUnits,
  useLandingsAuth,
} from "@ryde/utils-landings";

import {
  ClalWorkerType,
  useYupChooseStationLandingValidationSchema,
} from "./parts";

export const useStepChooseStationClal = () => {
  const { goToStep } = useLandingActions();
  const { getOfficialStationsByHomeAddress, setPassengerPickupStation } =
    useLandingApiStations();
  const { getUnitsDropdownItems } = useLandingApiUnits();
  const setStationsDropdownList = useSetRecoilState(
    landingDropdownStationListAtom,
  );
  const { yupChooseStationLandingValidationSchema } =
    useYupChooseStationLandingValidationSchema();
  const { t } = useTranslation();

  const workerTypeDropdownList = useMemo(
    () => [
      {
        // => id: LANDING_WORKERS_TYPE_IDS.Clal.employee,
        value: ClalWorkerType.Employee,
        label: t("landing.chooseStation.clalEmployee"),
      },
      {
        // => id: LANDING_WORKERS_TYPE_IDS.Clal.contractor,
        value: ClalWorkerType.Contractor,
        label: t("landing.chooseStation.clalContractor"),
      },
    ],
    [t],
  );

  const [unitsDropdownList, setUnitsDropdownList] = useState<IDropdownItem[]>(
    [],
  );

  const setServerErrors = useSetRecoilState(landingMessagesAtom);
  const setApiLoading = useSetRecoilState(apiDataFetchingLandingAtom);
  const { handleLogout } = useLandingsAuth();

  useEffect(() => {
    const loadUnitsDropdown = async (): Promise<void> => {
      try {
        const response = await getUnitsDropdownItems();
        const { success, data } = response ?? {};

        if (success && data) {
          setUnitsDropdownList(data);
        }
      } catch (error) {
        Log.warn("Failed to load units dropdown", error);
      }
    };

    loadUnitsDropdown();
  }, [getUnitsDropdownItems]);

  const resolver = useYupValidationResolver(
    yupChooseStationLandingValidationSchema,
    "landing",
  );

  const methods = useForm<IClalChooseStationLandingModel>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      workerId: undefined,
      firstName: "",
      lastName: "",
      phone: "",
      workerType: ClalWorkerType.Employee,
      homeAddress: {
        fullAddress: "",
        longitude: undefined,
        latitude: undefined,
      },
      sourceStation2Id: undefined,
    },
    resolver,
  });

  const {
    getValues,
    clearErrors,
    setError,
    setValue,
    trigger,
    watch,
    formState: { isValid },
  } = methods;

  const homeAddressWatchValue = watch(
    ClalChooseStationStepFormFieldsEnum.HomeAddress,
  );
  const sourceStation2IdWatchValue = watch(
    ClalChooseStationStepFormFieldsEnum.SourceStation2Id,
  );

  const handleHomeAddressChange = useEvent(async (): Promise<void> => {
    setServerErrors(undefined);
    const homeAddress = getValues(
      ClalChooseStationStepFormFieldsEnum.HomeAddress,
    );

    if (!homeAddress.latitude || !homeAddress.longitude) {
      setError(ClalChooseStationStepFormFieldsEnum.HomeAddress, {
        message: t("errors.missingCoordinates"),
      });
      setValue(ClalChooseStationStepFormFieldsEnum.SourceStation2Id, undefined);
      setStationsDropdownList([]);
      return;
    }

    try {
      const response = await getOfficialStationsByHomeAddress({
        homeAddress,
      });

      const { success, data } = response ?? {};

      if (success && data) {
        if (
          "isHomeAddressInPermittedPolygon" in data &&
          !data.isHomeAddressInPermittedPolygon
        ) {
          setError(ClalChooseStationStepFormFieldsEnum.HomeAddress, {
            message: t("errors.notInPermittedPolygon"),
          });
        }

        if (data.officialStationsList?.length) {
          setStationsDropdownList(
            data.officialStationsList as IDropdownItemStations[],
          );
        }

        setValue(
          ClalChooseStationStepFormFieldsEnum.SourceStation2Id,
          undefined,
        );
        clearErrors(ClalChooseStationStepFormFieldsEnum.SourceStation2Id);

        if (data.selectedStationId) {
          setValue(
            ClalChooseStationStepFormFieldsEnum.SourceStation2Id,
            data.selectedStationId,
            {
              shouldDirty: true,
              shouldValidate: true,
            },
          );
        } else {
          setError(ClalChooseStationStepFormFieldsEnum.SourceStation2Id, {
            message: t("errors.noNearbyStationFoundMessage"),
          });
        }
      }
    } catch (error) {
      const errorsNormalized = normalizeErrors(error as IApiError, t);
      setServerErrors(createMessage(errorsNormalized));
      Log.warnAndToast(errorsNormalized, error);
    }
  });

  const handleChooseStation = useEvent(async (): Promise<void> => {
    const isValidAfterTrigger = await trigger();

    if (!isValidAfterTrigger) {
      return;
    }

    const homeAddress = getValues(
      ClalChooseStationStepFormFieldsEnum.HomeAddress,
    );
    const sourceStation2Id = getValues(
      ClalChooseStationStepFormFieldsEnum.SourceStation2Id,
    );
    const workerId = getValues(ClalChooseStationStepFormFieldsEnum.WorkerId);
    const firstName = getValues(ClalChooseStationStepFormFieldsEnum.FirstName);
    const lastName = getValues(ClalChooseStationStepFormFieldsEnum.LastName);
    const phone = getValues(ClalChooseStationStepFormFieldsEnum.Phone);
    const unitId = getValues(ClalChooseStationStepFormFieldsEnum.Unit);
    const workerType = getValues(
      ClalChooseStationStepFormFieldsEnum.WorkerType,
    );

    if (sourceStation2Id && homeAddress) {
      setApiLoading(true);

      try {
        const response = await setPassengerPickupStation({
          homeAddress,
          sourceStation2Id,
          unitId,
          workerId,
          destinationStationId:
            LANDING_DESTINATION_STATIONS_IDS[LandingCompanyNameEnum.Clal],
          passengerTypeId:
            LANDING_WORKERS_TYPE_IDS[LandingCompanyNameEnum.Clal][
              workerType as ClalWorkerType
            ],
          firstName,
          lastName,
          phone,
        });

        const { success, error, data } = response ?? {};

        if (success && data) {
          setServerErrors(createMessage([]));
          goToStep(LandingStepEnum.FinishedStep);
          handleLogout();
          return;
        } else if (error && isTranslatableItem(error)) {
          const errorMessage = error.translationKey;
          setServerErrors(createMessage([errorMessage]));
          return;
        }
      } catch (error) {
        Log.error(
          "failed update passenger homeStation and sourceStation2Id. error:",
          error,
        );
      } finally {
        setApiLoading(false);
      }

      setServerErrors(createMessage(["errors.updateNotSuccess"]));
    }
  });

  const isContinueCanBeApply = useMemo(() => {
    return (
      isValid &&
      homeAddressWatchValue.fullAddress &&
      homeAddressWatchValue.latitude &&
      homeAddressWatchValue.longitude &&
      sourceStation2IdWatchValue
    );
  }, [
    homeAddressWatchValue.fullAddress,
    homeAddressWatchValue.latitude,
    homeAddressWatchValue.longitude,
    isValid,
    sourceStation2IdWatchValue,
  ]);

  return {
    methods,
    handleChooseStation,
    handleHomeAddressChange,
    isContinueCanBeApply,
    unitsDropdownList,
    workerTypeDropdownList,
  };
};
